import { DateTime, Duration } from 'luxon';
import humanizeDuration from 'humanize-duration';
import { describeBirthday } from './age';
import isDate from 'lodash/isDate';

import { parse } from 'date-fns';


export const tryParseDate = (dateString) => {
  if (isDate(dateString)) return dateString;
  if (!dateString) return null;

  if (typeof (dateString) === 'object') {
    return new Date(dateString.value);
  }

  // Treat an ISO value as a CALENDAR DATE: take the y-m-d exactly as
  // written and build a local-midnight Date for that day. The backend
  // serves dates at midnight in the SERVER timezone (e.g.
  // "2026-06-26T00:00:00.000+03:00"). `new Date(iso)` would convert that
  // instant into the viewer's timezone, so a GMT+2 user saw the previous
  // day for a value the GMT+3 server meant as the 26th (2026-06-22
  // report). Parsing the date part keeps the day stable for every viewer.
  //
  // A bare "1997-11-04" — what a `date` column serialises to, and what the
  // app itself now sends (CREW-1162) — goes down the same path; it used to
  // fall through to the dd.MM.yyyy branch and come back Invalid.
  const iso = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  if (dateString.includes('T')) return new Date(dateString);

  try {
    return parse(dateString, 'dd.MM.yyyy', new Date());
  } catch (e) {
    return null;
  }
};


// Is `value` a future date — i.e. a calendar day strictly AFTER
// today? Used by the crew-change "completed" guard. The old check
// `tryParseDate(value) > new Date()` compared INSTANTS, so a value
// dated *today* but carrying a time-of-day (or a UTC-midnight ISO
// string) read as "in the future" and blocked closing a crew change
// whose sign-off was today (2026-06-22 report). Compare against the
// END of today instead, so any moment within today's local day is
// allowed and only tomorrow+ is rejected.
export const isFutureDate = (value) => {
  const date = tryParseDate(value);
  if (!date || Number.isNaN(date.getTime())) return false;

  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  return date > endOfToday;
};


// CREW-1233 — how late a vacancy is, in the calendar days the table prints.
//
// Counted from `start` rather than from the vacancy's `state`: the backend
// flips `state` to `inactive` both when the joining date passes AND when a
// candidate is seated (`TimePlan#actual_vacancy_state`), so `state` cannot
// tell the two apart — and the gray stripe that leaned on it called 15 of 21
// rows "Closed — candidate seated" when they were merely overdue.
//
// Calendar days, not instants, for the same reason `isFutureDate` above
// compares against the end of today: a slot stored as `2026-08-27 21:00:00`
// UTC is the 28th in a UTC+3 office, and that later date is what the row
// shows. The day of joining itself is not late yet, so today counts as 0.
export const daysOverdue = (value) => {
  const date = tryParseDate(value);
  if (!date || Number.isNaN(date.getTime())) return 0;

  const startOfJoiningDay = new Date(date);
  startOfJoiningDay.setHours(0, 0, 0, 0);

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const days = Math.round((startOfToday - startOfJoiningDay) / 86400000);
  return days > 0 ? days : 0;
};

// The boolean form, for the places that only need "is it late".
export const isOverdue = (value) => daysOverdue(value) > 0;


// The day the user picked, as a plain "YYYY-MM-DD" — the only shape a date
// leaves this app in (CREW-1162). The picker yields a Date at LOCAL
// midnight, and `JSON.stringify` serialises that through `toJSON()` → UTC,
// where in any UTC+ zone it is the *previous* calendar day (26.06 00:00 +03
// → 25.06 21:00Z). The backend stored exactly that, so users learned to
// type +1 day. Reading the local parts sends the day they actually saw, and
// it lands correctly in a `date` column, in `.to_date`, and in a datetime
// column (midnight UTC).
//
// This replaces the local-noon anchoring added for the same class of bug on
// 2026-06-22: noon survived the round-trip, but it only ever guarded
// /timeplans and still stored a time nobody chose.
export const toApiDate = (value) => {
  const date = tryParseDate(value);
  if (!date || Number.isNaN(date.getTime())) return null;

  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};


export const convertDate = (date) => {
  const secondsInDay = 24 * 60 * 60;
  const pDate = tryParseDate(date);
  if (!pDate) return null;

  const dn = pDate.valueOf() / 1000 - 7200;
  const number = (dn + 5333472000) / secondsInDay;
  return Math.round(number);
};




/**
 * dateToFormat
 * @param date
 * @param format
 * @returns {string}
 */
// Parses with `tryParseDate` — the same function that renders these values in
// the pickers — rather than a naive `new Date()`. That difference is the whole
// bug of 2026-08-17: selecting a range on the scheduler prefills the create
// form with DayPilot.Date objects (the value lives in `.value`), the picker
// showed them correctly, and this formatter turned them into garbage. The
// dates the user could see never reached the request — first as the literal
// "Invalid DateTime" (500 in the vacancy callback), then, once that was
// dropped, as nothing at all (a dateless slot that white-screened the
// scheduler).
//
// Blank in, blank out. Unparseable in, `null` out — never a date-shaped
// string invented out of garbage.
export const dateToFormat = (date, format = 'dd.MM.yyyy') => {
  if (!date) return date;

  const parsed = tryParseDate(date);
  if (!parsed) return null;

  const luxon = DateTime.fromJSDate(parsed);

  return luxon.isValid ? luxon.toFormat(format) : null;
};

const DATE_TIME_FORMAT = 'dd.MM.yyyy HH:mm';

/**
 * dateStringToDateTime — "25.09.2026 13:41".
 *
 * A value that carries a time is an INSTANT (`created_at`, an arrival) and is
 * shown in the viewer's time. It must not go through `tryParseDate`, which
 * reads any ISO string as a calendar day (CREW-1162) — that turned every
 * timestamp into 00:00 (CREW-1188). A bare date keeps that calendar reading.
 * @param {String|Date} date
 * @return {string|null}
 */
export const dateStringToDateTime = (date) => {
  if (typeof date === 'string' && /\d{2}:\d{2}/.test(date)) {
    const instant = [DateTime.fromISO(date), DateTime.fromFormat(date, 'yyyy-MM-dd HH:mm:ss ZZZ')]
      .find((parsed) => parsed.isValid);
    if (instant) return instant.toLocal().toFormat(DATE_TIME_FORMAT);
  }
  return dateToFormat(date, DATE_TIME_FORMAT);
};

/**
 * "55 y.o." from a "dd.mm.yyyy" date of birth — the profile header's age.
 *
 * CREW-1079: this used to divide milliseconds by a year and ignored leap days,
 * so it added the year up to two weeks early (born 24.09.1971 read 55 from
 * 10 September) and printed "-3 y.o." for a date in the future. It now counts
 * on the calendar through utils/age, the rule the seafarers list uses.
 * @param {String} date
 * @returns {String} "" without a date, "check it" for an impossible one
 */
export const getDurationInYearByDate = (date) => {
  const info = describeBirthday(date);
  if (!info) return '';

  return info.plausible ? `${info.age} y.o.` : 'check it';
};
// next code for 3m24d format, I don't like this way
export const shortEnglishHumanizer = humanizeDuration
  .humanizer({
    language: 'shortEn',
    languages: {
      shortEn: {
        y: () => 'y',
        mo: () => 'm',
        w: () => 'w',
        d: () => 'd',
        h: () => 'h',
        m: () => 'min',
        s: () => 'sec',
        ms: () => 'ms',
      },
    },
  });

/**
 * getDurationFromSeconds
 * @param seconds
 * @returns {string}
 */
export const getDurationFromSeconds = (seconds) => {
  const durationInMilliSeconds = Duration.fromObject({ seconds }).as('milliseconds');

  return humanizeDuration(
    durationInMilliSeconds,
    { units: ['mo', 'd'], maxDecimalPoints: 0, conjunction: ' and ' },
  );

  // next code for 3m24d format, I don't like this way
  //   { units: ['mo', 'd'], maxDecimalPoints: 0, spacer: '', conjunction: '_' },
  // ).replace(/m_/g, 'm'); // because, "conjunction: ''" doesn't work (
};

/**
 * getDurationFromSeconds
 * @param {String|Date} start
 * @param {String|Date} end
 * @returns {string}
 */
export const getDurationByDates = (start, end) => getDurationFromSeconds(
  new Date(end).getTime() / 1000 - new Date(start).getTime() / 1000,
);

export default dateToFormat;
